import Analytics, { ACTION_ACTIVITY_START } from '@/scripts/analytics'
import { getActivityByHash } from '@/scripts/data/activityQueries'
import { getPageByHash } from '@/scripts/data/activityQueries'
import type { Activity, Page } from '@/scripts/data/types'

type StartActivityCallback = (activity: Activity) => void
type StartPageCallback = (page: Page) => void

type Handlers =
  | StartActivityCallback
  | {
      onActivity?: StartActivityCallback
      onPage?: StartPageCallback
    }

let _disposePrev: (() => void) | null = null
let lastBaseHash: string | null = null

export function initHashCheck(handlers: Handlers): () => void {
  _disposePrev?.()
  _disposePrev = null

  const callActivity = (a: Activity) => {
    if (typeof handlers === 'function') handlers(a)
    else handlers.onActivity?.(a)
  }
  const callPage = (p: Page) => {
    if (typeof handlers !== 'function') handlers.onPage?.(p)
  }

  const handleHashChange = (): void => {
    const raw = window.location.hash
    if (!raw) return
    const cleanHash = raw.substring(1)

    // Prevent infinite loop in AZ Kvíz: when quiz=junior is selected (the default quiz),
    // the quiz app removes it from the hash, triggering a hashchange here back and forth.
    // We skip reprocessing when only this param changes and the base hash stays the same.
    const baseHash = cleanHash.split('&')[0]

    if (baseHash === lastBaseHash) return
    lastBaseHash = baseHash

    // 1) Try PAGE first
    const page = getPageByHash(baseHash)
    if (page) {
      callPage(page)
      return
    }

    // 2) Then ACTIVITY (prefix match)
    const activity = getActivityByHash(baseHash) ?? findActivityByPartialHash(baseHash)

    if (!activity || !activity.isActivityOpen) return

    Analytics.sendCustomEvent(ACTION_ACTIVITY_START, activity.linkHash)
    callActivity(activity)
  }

  function findActivityByPartialHash(hash: string): Activity | null {
    const DP = window.DP as { activities?: Activity[] } | undefined
    const allActivities = DP?.activities || []
    return Array.isArray(allActivities)
      ? allActivities.find((a: Activity) => hash.startsWith(a.linkHash)) || null
      : null
  }

  window.addEventListener('hashchange', handleHashChange)
  handleHashChange()

  const dispose = () => {
    window.removeEventListener('hashchange', handleHashChange)
  }
  _disposePrev = dispose
  return dispose
}

export function clearHash() {
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname + window.location.search)
    lastBaseHash = ''
  }
}
